import { useEffect, useMemo, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'antd';
import c from 'classnames';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  addressDefaultValue,
  ContactFormValues,
} from 'src/components/domain/contacts/contact-form/contact-form.schema';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import {
  inlineFormInputStyles,
  InlineUnDecoratedTextInput,
} from 'src/components/general/inputs/inline-text-inputs';
import { OverlayFooterActionButtons } from 'src/components/general/overlay/overlay-footer-action-buttons';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { ellipsisTextStyle } from 'src/components/pages/dashboard/components/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';

const AddressInputTrigger = styled(FlexRow)`
  max-width: 100%;
  min-width: 0;
`;

const AddressInputDisplay = styled.div`
  ${inlineFormInputStyles};
  cursor: pointer;
  height: auto;
  min-height: 32px;
  min-width: 0;
  overflow: hidden;
  text-align: right;

  ${up('md')} {
    text-align: left;
  }

  &.focus-within {
    background: ${({ theme }) => theme.colors.porcelain};
    border-color: ${({ theme }) => theme.colors.porcelain};
    box-shadow: none;
  }
`;

const AddressInputLine = styled.span`
  ${ellipsisTextStyle};
  display: block;
`;

const AddressInputPlaceholder = styled(AddressInputLine)`
  color: ${({ theme }) => theme.colors.geyser};
`;

const ContactAddressOverlayContainer = styled(FlexColumn)`
  padding: 15px;
  gap: 10px;

  ${up('md')} {
    gap: 3px;
    padding: 5px;
  }
`;
type AddressInputProps = {
  field: ControllerRenderProps<ContactFormValues, 'address'>;
  defaultValues: ContactFormValues['address'];
};

export const ContactAddressInput = ({ field, defaultValues }: AddressInputProps) => {
  const [addressDraft, setAddressDraft] = useState<ContactFormValues['address']>(
    defaultValues ?? addressDefaultValue,
  );
  const addressVisibilityHandler = useVisible();
  const breakpointUpMd = useBreakpoint(up('md'));
  const { t } = useTranslation(['contacts', 'common']);

  useEffect(() => {
    if (addressVisibilityHandler.visible) {
      setAddressDraft(field.value ?? addressDefaultValue);
    }
  }, [addressVisibilityHandler.visible, field.value]);

  const footerContent = useMemo(
    () => (
      <OverlayFooterActionButtons
        baseTestId="address-form-footer-action-buttons"
        onReset={() => {
          setAddressDraft(addressDefaultValue);
          field.onChange(addressDefaultValue);
          addressVisibilityHandler.close();
        }}
        onSubmit={() => {
          field.onChange(addressDraft);
          addressVisibilityHandler.close();
        }}
      />
    ),
    [addressDraft, addressVisibilityHandler, field],
  );

  const contactAddressOverlay = useMemo(
    () => (
      <ContactAddressOverlayContainer>
        <InlineUnDecoratedTextInput
          leadingIcon="location"
          data-lgg-id="add-contact-field-address-street"
          value={addressDraft?.address}
          placeholder={t('contacts:createContactModal.fields.address.street.placeholder')}
          onClick={addressVisibilityHandler.show}
          onChange={(event) => {
            setAddressDraft((prev) => ({
              ...(prev ?? addressDefaultValue),
              address: event.target.value,
            }));
          }}
        />
        <InlineUnDecoratedTextInput
          leadingIcon="city"
          data-lgg-id="add-contact-field-address-city"
          value={addressDraft?.city}
          placeholder={t('contacts:createContactModal.fields.address.city.placeholder')}
          onClick={addressVisibilityHandler.show}
          onChange={(event) => {
            setAddressDraft((prev) => ({
              ...(prev ?? addressDefaultValue),
              city: event.target.value,
            }));
          }}
        />
        <InlineUnDecoratedTextInput
          leadingIcon="state"
          data-lgg-id="add-contact-field-address-state"
          value={addressDraft?.state}
          placeholder={t('contacts:createContactModal.fields.address.state.placeholder')}
          onClick={addressVisibilityHandler.show}
          onChange={(event) => {
            setAddressDraft((prev) => ({
              ...(prev ?? addressDefaultValue),
              state: event.target.value,
            }));
          }}
        />
        <InlineUnDecoratedTextInput
          leadingIcon="world"
          data-lgg-id="add-contact-field-address-country"
          value={addressDraft?.country}
          placeholder={t(
            'contacts:createContactModal.fields.address.country.placeholder',
          )}
          onClick={addressVisibilityHandler.show}
          onChange={(event) => {
            setAddressDraft((prev) => ({
              ...(prev ?? addressDefaultValue),
              country: event.target.value,
            }));
          }}
        />
        <InlineUnDecoratedTextInput
          leadingIcon="postalCode"
          data-lgg-id="add-contact-field-address-postal-code"
          value={addressDraft?.postalCode}
          placeholder={t(
            'contacts:createContactModal.fields.address.zipCode.placeholder',
          )}
          onClick={addressVisibilityHandler.show}
          onChange={(event) => {
            setAddressDraft((prev) => ({
              ...(prev ?? addressDefaultValue),
              postalCode: event.target.value,
            }));
          }}
        />
      </ContactAddressOverlayContainer>
    ),
    [
      addressDraft?.address,
      addressDraft?.city,
      addressDraft?.country,
      addressDraft?.postalCode,
      addressDraft?.state,
      addressVisibilityHandler.show,
      t,
    ],
  );

  const contactAddressTrigger = useMemo(() => {
    const addressLines = [
      field.value?.address,
      field.value?.city,
      field.value?.state,
      [field.value?.postalCode, field.value?.country].filter(Boolean).join(', '),
    ].filter((line): line is string => Boolean(line));

    return (
      <AddressInputTrigger onClick={addressVisibilityHandler.show}>
        <AddressInputDisplay
          data-lgg-id="add-contact-field-address-trigger"
          className={c({ 'focus-within': addressVisibilityHandler.visible })}
        >
          {addressLines.length > 0 ? (
            addressLines.map((line) => (
              <AddressInputLine key={line} title={line}>
                {line}
              </AddressInputLine>
            ))
          ) : (
            <AddressInputPlaceholder>
              {t('contacts:createContactModal.fields.address.placeholder')}
            </AddressInputPlaceholder>
          )}
        </AddressInputDisplay>
      </AddressInputTrigger>
    );
  }, [
    addressVisibilityHandler.show,
    addressVisibilityHandler.visible,
    field.value?.address,
    field.value?.city,
    field.value?.country,
    field.value?.postalCode,
    field.value?.state,
    t,
  ]);

  return breakpointUpMd ? (
    <Dropdown
      getPopupContainer={(targetElement) => targetElement}
      overlayClassName="context-menu"
      visible={addressVisibilityHandler.visible}
      trigger={['click']}
      onVisibleChange={(isVisible) => {
        if (!isVisible) {
          addressVisibilityHandler.close();
        }
      }}
      placement="bottomCenter"
      overlay={
        <FlexColumn>
          {contactAddressOverlay}
          {footerContent}
        </FlexColumn>
      }
    >
      {contactAddressTrigger}
    </Dropdown>
  ) : (
    <>
      {contactAddressTrigger}
      <BottomDrawer
        title={t('common:address')}
        onClose={addressVisibilityHandler.close}
        visible={addressVisibilityHandler.visible}
        fullHeight
        footer={footerContent}
      >
        {contactAddressOverlay}
      </BottomDrawer>
    </>
  );
};
